import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import NotFound from './pages/NotFound';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import axios from 'axios';
import MeniPdf from './pages/MeniPdf';




// Lazy load stranice
const Home = lazy(() => import('./pages/Home'));
const Menu = lazy(() => import('./pages/Menu'));
const Reservations = lazy(() => import('./pages/Reservations'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Cart = lazy(() => import('./pages/Cart'));
const Admin = lazy(() => import('./pages/Admin'));
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';



// Loading fallback komponenta
const PageLoader = () => (
  <div className="page-loader">
    <div className="loader-spinner"></div>
  </div>
);

const AppContent = () => {
  useEffect(() => {
    axios.post(`${API}/visits/track`).catch(() => {});
  }, []);
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/admin/*" element={<Admin />} />
          <Route path="/*" element={
            <>
              <Navbar />
              <div className="app-content revealed">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/meni" element={<Menu />} />
                  <Route path="/rezervacije" element={<Reservations />} />
                  <Route path="/galerija" element={<Gallery />} />
                  <Route path="/kontakt" element={<Contact />} />
                  <Route path="/o-nama" element={<About />} />
                  <Route path="/korpa" element={<Cart />} />
                  <Route path="/meni-pdf" element={<MeniPdf />} />
                  <Route path="*" element={<NotFound />} />  {/* Catch-all za 404 */}
                </Routes>
                <Footer />
              </div>
            </>
          } />
        </Routes>
      </Suspense>
    </>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  const [hiding, setHiding] = useState(false);
  
  // Proveri da li smo na admin ruti - ako jesmo, preskoči loading
  const isAdminRoute = window.location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminRoute) {
      setLoading(false);
      return;
    }

    let minTimerDone = false;
    let pageLoaded = false;

    const tryHide = () => {
      if (minTimerDone && pageLoaded) {
        setHiding(true);
        setTimeout(() => setLoading(false), 1200);
      }
    };

    // Minimalno vreme za animaciju (800ms)
    const timer = setTimeout(() => {
      minTimerDone = true;
      tryHide();
    }, 800);

    // Čekaj pravo učitavanje stranice
    const handleLoad = () => {
      pageLoaded = true;
      tryHide();
    };

    if (document.readyState === 'complete') {
      pageLoaded = true;
    } else {
      window.addEventListener('load', handleLoad);
    }

    // Ako je stranica već učitana, proveri odmah
    if (pageLoaded) tryHide();

    return () => {
      clearTimeout(timer);
      window.removeEventListener('load', handleLoad);
    };
  }, [isAdminRoute]);

  return (
    <ErrorBoundary>
    <HelmetProvider>
    <AuthProvider>
      <CurrencyProvider>
        <CartProvider>

          {/* ═══ LOADING SCREEN - samo za ne-admin rute ═══ */}
          {loading && !isAdminRoute && (
            <div className={`loading-screen ${hiding ? 'hide' : ''}`}>
              <div className="loading-logo-wrapper">
                <div className="loading-gold-line" />
                <img src="https://res.cloudinary.com/dnx6y0737/image/upload/v1775900226/Untitled_design_1_cziq8t.png" alt="Logo" className="loading-logo-img" />
                <p className="loading-tagline">RESTAURANT</p>
                <div className="loading-gold-line" />
                <div className="loading-dots">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}

          {/* ═══ MAIN CONTENT ═══ */}
          <Router>
            <AppContent />
          </Router>

        </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;